/* ==========================================================================
   BARE — theme.js
   Original vanilla JS for the BARE ORIGINALS Shopify theme. No dependencies.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const strings = theme.strings || {};
  const routes = theme.routes || {};
  const settings = theme.settings || {};

  /* ---------- Utilities ---------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const once = (el, key) => {
    const flag = `init${key}`;
    if (el.dataset[flag]) return false;
    el.dataset[flag] = '1';
    return true;
  };
  const storage = {
    get(key, fallback) { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch (e) { return fallback; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage unavailable */ } }
  };

  function formatMoney(cents, format = theme.moneyFormat || '{{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (num, decimals, thousands = ',', decimal = '.') => {
      const fixed = (num / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      const withSep = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return frac ? withSep + decimal + frac : withSep;
    };
    const match = format.match(placeholder);
    let value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  let toastTimer;
  function toast(message) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 2600);
  }

  /* ---------- Scroll lock ---------- */
  let lockCount = 0;
  const lockScroll = () => { lockCount += 1; document.body.classList.add('is-locked'); };
  const unlockScroll = () => { lockCount = Math.max(0, lockCount - 1); if (!lockCount) document.body.classList.remove('is-locked'); };

  /* ---------- Drawers & modals ---------- */
  let lastFocus = null;
  function openOverlay(el) {
    if (!el || el.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    lockScroll();
    const focusTarget = $('[data-predictive-search-input], input[type="search"], .drawer__close, .modal__close, button, a', el);
    if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 60);
  }
  function closeOverlay(el) {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    unlockScroll();
    $$('video', el).forEach((v) => v.pause());
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }
  const openDrawer = (id) => openOverlay(document.getElementById(id));

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); openDrawer(opener.dataset.drawerOpen); return; }
    const modalOpener = e.target.closest('[data-modal-open]');
    if (modalOpener) { e.preventDefault(); openOverlay(document.getElementById(modalOpener.dataset.modalOpen)); return; }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) { closeOverlay(closer.closest('[data-drawer]')); return; }
    const modalCloser = e.target.closest('[data-modal-close]');
    if (modalCloser) { closeOverlay(modalCloser.closest('[data-modal]')); return; }
    const cartToggle = e.target.closest('[data-cart-toggle]');
    if (cartToggle && settings.cartType === 'drawer' && document.getElementById('CartDrawer')) {
      e.preventDefault();
      openDrawer('CartDrawer');
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const lightbox = $('[data-lightbox].is-open');
    if (lightbox) { closeLightbox(); return; }
    const open = $$('[data-drawer].is-open, [data-modal].is-open').pop();
    if (open) closeOverlay(open);
  });

  /* ---------- Header ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header || !once(header, 'Header')) return;
    const mode = header.dataset.sticky;
    if (mode === 'always' || mode === 'reveal') header.classList.add('is-sticky');

    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 10);
      if (mode === 'reveal') {
        const hide = y > lastY && y > header.offsetHeight * 2 && !$('.header__menu-item.is-open', header);
        header.classList.toggle('is-hidden', hide);
      }
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    $$('[data-menu-item]', header).forEach((item) => {
      const panel = $('[data-mega-menu], [data-dropdown]', item);
      if (!panel) return;
      const link = $('.header__menu-link', item);
      let timer;
      const open = () => {
        clearTimeout(timer);
        $$('.header__menu-item.is-open', header).forEach((o) => { if (o !== item) close(o); });
        item.classList.add('is-open');
        link.setAttribute('aria-expanded', 'true');
      };
      const close = (target = item) => {
        target.classList.remove('is-open');
        const l = $('.header__menu-link', target);
        if (l) l.setAttribute('aria-expanded', 'false');
      };
      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', () => { timer = setTimeout(() => close(), 120); });
      item.addEventListener('focusin', open);
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
      link.addEventListener('click', (e) => {
        if (window.matchMedia('(hover: none)').matches && !item.classList.contains('is-open')) {
          e.preventDefault();
          open();
        }
      });
    });
  }

  /* ---------- Localization selects ---------- */
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-localization-select]');
    if (select) select.form.submit();
  });

  /* ---------- Announcement bar ---------- */
  function initAnnouncement(root = document) {
    $$('[data-announcement]', root).forEach((bar) => {
      if (!once(bar, 'Announcement')) return;
      const slides = $$('[data-announcement-slide]', bar);
      if (slides.length < 2) return;
      let index = 0;
      let timer;
      const show = (i) => {
        slides[index].classList.remove('is-active');
        index = (i + slides.length) % slides.length;
        slides[index].classList.add('is-active');
      };
      const speed = (parseInt(bar.dataset.autoplay, 10) || 5) * 1000;
      const start = () => { clearInterval(timer); timer = setInterval(() => show(index + 1), speed); };
      const prev = $('[data-announcement-prev]', bar);
      const next = $('[data-announcement-next]', bar);
      if (prev) prev.addEventListener('click', () => { show(index - 1); start(); });
      if (next) next.addEventListener('click', () => { show(index + 1); start(); });
      bar.addEventListener('mouseenter', () => clearInterval(timer));
      bar.addEventListener('mouseleave', start);
      bar.goTo = (i) => { show(i); clearInterval(timer); };
      start();
    });
  }

  /* ---------- Slideshow ---------- */
  function initSlideshows(root = document) {
    $$('[data-slideshow]', root).forEach((hero) => {
      if (!once(hero, 'Slideshow')) return;
      const slides = $$('[data-slide]', hero);
      const dots = $$('[data-slide-dot]', hero);
      if (slides.length < 2) return;
      const speed = parseInt(hero.dataset.speed, 10) || 6000;
      const autoplay = hero.dataset.autoplay === 'true';
      hero.style.setProperty('--slide-speed', `${speed}ms`);
      let index = 0;
      let timer;
      const goTo = (i) => {
        slides[index].classList.remove('is-active');
        if (dots[index]) dots[index].classList.remove('is-active');
        index = (i + slides.length) % slides.length;
        slides[index].classList.add('is-active');
        if (dots[index]) dots[index].classList.add('is-active');
        slides.forEach((sl, j) => { if (j !== index) $$('video', sl).forEach((v) => v.pause()); });
        if (!prefersLessMotion()) $$('video', slides[index]).forEach((v) => { v.play().catch(() => {}); });
      };
      const start = () => { if (!autoplay) return; clearInterval(timer); timer = setInterval(() => goTo(index + 1), speed); };
      dots.forEach((dot, i) => dot.addEventListener('click', () => { goTo(i); start(); }));
      const prev = $('[data-slide-prev]', hero);
      const next = $('[data-slide-next]', hero);
      if (prev) prev.addEventListener('click', () => { goTo(index - 1); start(); });
      if (next) next.addEventListener('click', () => { goTo(index + 1); start(); });
      let startX = 0;
      hero.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      hero.addEventListener('touchend', (e) => {
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) { goTo(index + (diff > 0 ? 1 : -1)); start(); }
      });
      hero.goTo = (i) => { goTo(i); clearInterval(timer); };
      start();
    });
  }

  /* ---------- Generic sliders ---------- */
  function initSliders(root = document) {
    $$('[data-slider]', root).forEach((slider) => {
      if (!once(slider, 'Slider')) return;
      const track = $('[data-slider-track]', slider);
      const prev = $('[data-slider-prev]', slider);
      const next = $('[data-slider-next]', slider);
      if (!track || (!prev && !next)) return;
      const step = () => {
        const item = track.children[0];
        if (!item) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return (item.getBoundingClientRect().width + gap) * Math.max(1, Math.floor(track.clientWidth / item.getBoundingClientRect().width));
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      };
      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  }

  /* ---------- Tabs ---------- */
  function initTabs(root = document) {
    $$('[data-tabs]', root).forEach((wrap) => {
      if (!once(wrap, 'Tabs')) return;
      const tabs = $$('[data-tab]', wrap);
      const panels = $$('[data-tab-panel]', wrap);
      const select = (i) => {
        tabs.forEach((t, j) => { t.classList.toggle('is-active', i === j); t.setAttribute('aria-selected', i === j ? 'true' : 'false'); });
        panels.forEach((p, j) => { p.hidden = i !== j; });
        revealIn(panels[i]);
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
            tabs[n].focus(); select(n);
          }
        });
      });
    });
  }

  /* ---------- Cart ---------- */
  const cart = {
    sectionsToRender() {
      const ids = [];
      if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
      const page = $('[data-cart-page]');
      if (page) ids.push(page.dataset.sectionId);
      return ids;
    },
    async request(url, body) {
      const isForm = body instanceof FormData;
      const res = await fetch(url, {
        method: 'POST',
        headers: isForm ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: isForm ? body : JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        const message = data.description || data.message || strings.cartError;
        throw new Error(typeof message === 'string' ? message : strings.cartError);
      }
      return data;
    },
    render(sections) {
      if (!sections) return;
      const drawerHTML = sections['cart-drawer'];
      if (drawerHTML) {
        const doc = parseHTML(drawerHTML);
        const fresh = $('[data-cart-drawer-inner]', doc);
        const current = $('[data-cart-drawer-inner]');
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          current.dataset.cartCountValue = fresh.dataset.cartCountValue;
          this.updateCount(parseInt(fresh.dataset.cartCountValue, 10) || 0);
          initScope(current);
        }
      }
      const page = $('[data-cart-page]');
      if (page && sections[page.dataset.sectionId]) {
        const doc = parseHTML(sections[page.dataset.sectionId]);
        const fresh = $('[data-cart-page]', doc);
        if (fresh) page.innerHTML = fresh.innerHTML;
      }
    },
    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
      });
    },
    afterAdd(data) {
      this.render(data.sections);
      if (settings.cartType === 'page') {
        window.location.href = routes.cart;
        return;
      }
      closeOverlay($('#QuickViewModal'));
      openDrawer('CartDrawer');
    },
    async add(formData) {
      const sections = this.sectionsToRender();
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
      const data = await this.request(`${routes.cartAdd}.js`, formData);
      this.afterAdd(data);
      return data;
    },
    async change(line, quantity) {
      const data = await this.request(`${routes.cartChange}.js`, {
        line, quantity, sections: this.sectionsToRender(), sections_url: window.location.pathname
      });
      this.render(data.sections);
      if (!$('[data-cart-page]') && data.item_count === 0) this.updateCount(0);
      return data;
    },
    updateNote: debounce((note) => {
      fetch(`${routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) });
    }, 500)
  };

  function setLoading(btn, loading) {
    if (!btn) return;
    btn.classList.toggle('is-loading', loading);
    btn.disabled = loading;
    btn.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  // Product forms
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    if (settings.cartType === 'page' && !window.fetch) return;
    e.preventDefault();
    const btn = $('[data-add-to-cart]', form);
    const error = $('[data-form-error]', form);
    if (btn && btn.disabled) return;
    if (error) error.hidden = true;
    setLoading(btn, true);
    try {
      await cart.add(new FormData(form));
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; } else { toast(err.message); }
    } finally {
      setLoading(btn, false);
    }
  });

  // Quick add & cart line changes
  document.addEventListener('click', async (e) => {
    const quickAdd = e.target.closest('[data-quick-add]');
    if (quickAdd) {
      e.preventDefault();
      const fd = new FormData();
      fd.append('id', quickAdd.dataset.quickAdd);
      fd.append('quantity', '1');
      setLoading(quickAdd, true);
      try { await cart.add(fd); } catch (err) { toast(err.message); } finally { setLoading(quickAdd, false); }
      return;
    }
    const change = e.target.closest('[data-cart-change]');
    if (change) {
      e.preventDefault();
      const line = parseInt(change.dataset.cartChange, 10);
      const qty = Math.max(0, parseInt(change.dataset.quantity, 10) || 0);
      const row = change.closest('.cart-line');
      if (row) row.classList.add('is-loading');
      try { await cart.change(line, qty); } catch (err) { toast(err.message); if (row) row.classList.remove('is-loading'); }
    }
  });

  document.addEventListener('change', (e) => {
    const qtyInput = e.target.closest('[data-cart-qty-input]');
    if (qtyInput) cart.change(parseInt(qtyInput.dataset.cartQtyInput, 10), Math.max(0, parseInt(qtyInput.value, 10) || 0)).catch((err) => toast(err.message));
  });
  document.addEventListener('input', (e) => {
    const note = e.target.closest('[data-cart-note]');
    if (note) cart.updateNote(note.value);
  });

  // Quantity steppers on product forms
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const input = $('input', btn.closest('[data-qty]'));
    if (!input) return;
    const next = Math.max(parseInt(input.min, 10) || 1, (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qtyChange, 10));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Product: variants ---------- */
  function getVariants(picker) {
    if (!picker._variants) {
      try { picker._variants = JSON.parse($('[data-variants-json]', picker).textContent); } catch (e) { picker._variants = []; }
    }
    return picker._variants;
  }

  function selectedOptions(picker) {
    const options = [];
    $$('fieldset.variant-field, .variant-field', picker).forEach((field) => {
      const checked = $('input:checked', field);
      const select = $('select', field);
      if (checked) options[parseInt(checked.dataset.optionIndex, 10)] = checked.value;
      else if (select) options[parseInt(select.dataset.optionIndex, 10)] = select.value;
    });
    return options;
  }

  function markAvailability(picker, variants, options) {
    $$('input[data-option-index]', picker).forEach((input) => {
      const idx = parseInt(input.dataset.optionIndex, 10);
      const available = variants.some((v) => v.available && v.options[idx] === input.value && options.every((val, j) => j === idx || val === undefined || v.options[j] === val || j > idx));
      input.classList.toggle('is-unavailable', !available);
    });
    $$('select[data-option-index]', picker).forEach((select) => {
      const idx = parseInt(select.dataset.optionIndex, 10);
      $$('option', select).forEach((opt) => {
        const ok = variants.some((v) => v.available && v.options[idx] === opt.value && options.every((val, j) => j === idx || val === undefined || v.options[j] === val || j > idx));
        opt.textContent = opt.value + (ok ? '' : ` — ${strings.unavailable}`);
      });
    });
  }

  function priceHTML(variant) {
    const onSale = variant.compare_at_price > variant.price;
    return `<div class="price${onSale ? ' price--on-sale' : ''}${variant.available ? '' : ' price--sold-out'}">
      <span class="price__current">${formatMoney(variant.price)}</span>
      ${onSale ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : ''}
    </div>`;
  }

  function setButtonState(scope, variant) {
    $$('[data-add-to-cart]', scope).forEach((btn) => {
      const label = $('[data-add-to-cart-text]', btn) || btn;
      btn.disabled = !variant || !variant.available;
      label.textContent = !variant ? strings.unavailable : (variant.available ? strings.addToCart : strings.soldOut);
    });
    $$('[data-sticky-atc-btn]', scope).forEach((btn) => {
      btn.disabled = !variant || !variant.available;
      btn.textContent = !variant ? strings.unavailable : (variant.available ? strings.addToCart : strings.soldOut);
    });
    $$('[data-variant-id]', scope).forEach((input) => {
      if (variant) input.value = variant.id;
      input.disabled = !variant;
    });
  }

  const renderControllers = new WeakMap();
  async function renderVariantSection(section, variant) {
    const sectionId = section.dataset.sectionId;
    const url = section.dataset.productUrl;
    if (!sectionId || !url) return;
    const scope = section.closest('.shopify-section') || section;
    const prev = renderControllers.get(section);
    if (prev) prev.abort();
    const controller = new AbortController();
    renderControllers.set(section, controller);
    try {
      const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}variant=${variant.id}&section_id=${encodeURIComponent(sectionId)}`, { signal: controller.signal });
      const doc = parseHTML(await res.text());
      $$('[data-swap]', scope).forEach((el) => {
        const fresh = $(`[data-swap="${el.dataset.swap}"]`, doc);
        if (fresh && !el.closest('[data-sticky-atc]')) el.innerHTML = fresh.innerHTML;
        else if (fresh && el.closest('[data-sticky-atc]') && el.dataset.swap === 'price') el.innerHTML = fresh.innerHTML;
      });
      setButtonState(scope, variant);
      if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
    } catch (err) {
      if (err.name !== 'AbortError') {
        $$('[data-swap="price"]', scope).forEach((el) => { el.innerHTML = priceHTML(variant); });
        setButtonState(scope, variant);
      }
    }
  }

  function onVariantChange(picker) {
    const section = picker.closest('[data-product-section]');
    const scope = (section && section.closest('.shopify-section')) || section || picker.parentElement;
    const variants = getVariants(picker);
    const options = selectedOptions(picker);
    const variant = variants.find((v) => v.options.every((val, i) => val === options[i]));

    $$('fieldset.variant-field', picker).forEach((field) => {
      const checked = $('input:checked', field);
      const label = $('[data-selected-value]', field);
      if (checked && label) label.textContent = checked.value;
    });
    markAvailability(picker, variants, options);

    if (!variant) { setButtonState(scope, null); return; }

    // Media
    if (variant.featured_media && section) {
      const gallery = $('[data-gallery]', section);
      if (gallery && gallery.goToMedia) gallery.goToMedia(variant.featured_media.id);
    }

    // URL for the main product page
    if (section && section.closest('.section-main-product')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      const pickup = $('[data-pickup-availability]', section);
      if (pickup) { pickup.dataset.variantId = variant.id; loadPickup(pickup); }
    }

    // Featured product sections render client-side; product page & quick view use section rendering.
    const renderable = section && (section.closest('.section-main-product') || section.dataset.sectionId === 'quick-view');
    if (renderable) {
      setButtonState(scope, variant);
      renderVariantSection(section, variant);
    } else {
      $$('[data-swap="price"]', scope).forEach((el) => { el.innerHTML = priceHTML(variant); });
      setButtonState(scope, variant);
    }
  }

  document.addEventListener('change', (e) => {
    const picker = e.target.closest('[data-variant-picker]');
    if (picker && e.target.matches('[data-option-index]')) onVariantChange(picker);
  });

  function initVariantPickers(root = document) {
    $$('[data-variant-picker]', root).forEach((picker) => {
      if (!once(picker, 'Picker')) return;
      markAvailability(picker, getVariants(picker), selectedOptions(picker));
    });
  }

  /* ---------- Product: gallery ---------- */
  function initGalleries(root = document) {
    $$('[data-gallery]', root).forEach((gallery) => {
      if (!once(gallery, 'Gallery')) return;
      const track = $('[data-gallery-track]', gallery);
      const items = $$('[data-media-index]', gallery);
      const thumbs = $$('[data-thumb-index]', gallery);
      const dots = $$('[data-dot-index]', gallery);
      const counter = $('[data-gallery-current]', gallery);
      if (!track) return;
      let current = Math.max(0, items.findIndex((i) => i.classList.contains('is-active')));

      const isStacked = () => gallery.classList.contains('gallery--grid') && window.matchMedia('(min-width: 990px)').matches;
      const setActive = (i) => {
        current = i;
        items.forEach((item, j) => item.classList.toggle('is-active', j === i));
        thumbs.forEach((t, j) => t.classList.toggle('is-active', j === i));
        dots.forEach((d, j) => { d.classList.toggle('is-active', j === i); d.setAttribute('aria-selected', j === i ? 'true' : 'false'); });
        if (counter) counter.textContent = i + 1;
        const thumb = thumbs[i];
        if (thumb && thumb.parentElement && thumb.parentElement.parentElement) {
          const strip = thumb.parentElement.parentElement;
          strip.scrollTo({ left: thumb.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'smooth' });
        }
        $$('video', track).forEach((v, j) => { if (!items[i] || !items[i].contains(v)) v.pause(); });
      };
      const goTo = (i) => {
        if (!items.length) return;
        const idx = (i + items.length) % items.length;
        if (isStacked()) {
          items[idx].scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          track.scrollTo({ left: items[idx].offsetLeft - track.offsetLeft, behavior: 'smooth' });
        }
        setActive(idx);
      };
      gallery.goToMedia = (mediaId) => {
        const idx = items.findIndex((item) => item.dataset.mediaId === String(mediaId));
        if (idx > -1) goTo(idx);
      };

      track.addEventListener('scroll', debounce(() => {
        if (isStacked()) return;
        const idx = Math.round(track.scrollLeft / track.clientWidth);
        if (idx !== current && items[idx]) setActive(idx);
      }, 60), { passive: true });
      thumbs.forEach((t) => t.addEventListener('click', () => goTo(parseInt(t.dataset.thumbIndex, 10))));
      dots.forEach((d) => d.addEventListener('click', () => goTo(parseInt(d.dataset.dotIndex, 10))));
      const prev = $('[data-gallery-prev]', gallery);
      const next = $('[data-gallery-next]', gallery);
      if (prev) prev.addEventListener('click', () => goTo(current - 1));
      if (next) next.addEventListener('click', () => goTo(current + 1));

      if (current > 0 && !isStacked()) requestAnimationFrame(() => { track.scrollLeft = items[current].offsetLeft - track.offsetLeft; });

      const lightboxData = $('[data-lightbox-images]', gallery);
      if (lightboxData) {
        let images = [];
        try { images = JSON.parse(lightboxData.textContent); } catch (e) { images = []; }
        $$('[data-lightbox-open]', gallery).forEach((btn) => {
          btn.addEventListener('click', () => openLightbox(images, parseInt(btn.dataset.lightboxOpen, 10)));
        });
      }
    });
  }

  /* ---------- Lightbox ---------- */
  const lightbox = { images: [], index: 0 };
  function renderLightbox() {
    const el = $('[data-lightbox]');
    const stage = $('[data-lightbox-stage]', el);
    stage.classList.remove('is-zoomed');
    stage.innerHTML = `<img src="${lightbox.images[lightbox.index]}" alt="">`;
  }
  function openLightbox(images, index) {
    const el = $('[data-lightbox]');
    if (!el) return;
    lightbox.images = images.filter(Boolean);
    const target = images[index];
    lightbox.index = Math.max(0, lightbox.images.indexOf(target));
    renderLightbox();
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    lockScroll();
  }
  function closeLightbox() {
    const el = $('[data-lightbox]');
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    unlockScroll();
  }
  function initLightbox() {
    const el = $('[data-lightbox]');
    if (!el || !once(el, 'Lightbox')) return;
    const step = (d) => { if (!lightbox.images.length) return; lightbox.index = (lightbox.index + d + lightbox.images.length) % lightbox.images.length; renderLightbox(); };
    $('[data-lightbox-close]', el).addEventListener('click', closeLightbox);
    $('[data-lightbox-prev]', el).addEventListener('click', () => step(-1));
    $('[data-lightbox-next]', el).addEventListener('click', () => step(1));
    const stage = $('[data-lightbox-stage]', el);
    stage.addEventListener('click', (e) => {
      const zoomed = stage.classList.toggle('is-zoomed');
      if (zoomed) {
        const rect = stage.getBoundingClientRect();
        requestAnimationFrame(() => {
          stage.scrollLeft = ((e.clientX - rect.left) / rect.width) * (stage.scrollWidth - stage.clientWidth);
          stage.scrollTop = ((e.clientY - rect.top) / rect.height) * (stage.scrollHeight - stage.clientHeight);
        });
      }
    });
    document.addEventListener('keydown', (e) => {
      if (!el.classList.contains('is-open')) return;
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    });
    let startX = 0;
    el.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    el.addEventListener('touchend', (e) => {
      if (stage.classList.contains('is-zoomed')) return;
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) step(diff > 0 ? 1 : -1);
    });
  }

  /* ---------- Quick view ---------- */
  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (!trigger) return;
    e.preventDefault();
    const modal = $('#QuickViewModal');
    const content = $('[data-quick-view-content]', modal);
    if (!modal || !content) { window.location.href = trigger.dataset.quickView; return; }
    content.innerHTML = '<div class="modal-loading"></div>';
    if (!modal.classList.contains('is-open')) openOverlay(modal);
    try {
      const url = new URL(trigger.dataset.quickView, window.location.origin);
      url.searchParams.set('section_id', 'quick-view');
      const res = await fetch(url.toString());
      const doc = parseHTML(await res.text());
      const qv = $('.quick-view', doc);
      if (!qv) throw new Error('Quick view unavailable');
      content.innerHTML = '';
      content.appendChild(qv);
      initScope(content);
      if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
    } catch (err) {
      window.location.href = trigger.dataset.quickView;
    }
  });

  /* ---------- Card swatches ---------- */
  document.addEventListener('mouseover', (e) => {
    const swatch = e.target.closest('[data-swatch-image]');
    if (!swatch || !swatch.dataset.swatchImage) return;
    const card = swatch.closest('[data-product-card]');
    const img = card && $('.card__img--primary', card);
    if (!img) return;
    if (!img.dataset.originalSrc) { img.dataset.originalSrc = img.currentSrc || img.src; img.dataset.originalSrcset = img.srcset; }
    img.srcset = '';
    img.src = swatch.dataset.swatchImage;
  });
  document.addEventListener('mouseout', (e) => {
    const card = e.target.closest('[data-product-card]');
    if (!card || card.contains(e.relatedTarget)) return;
    const img = $('.card__img--primary', card);
    if (img && img.dataset.originalSrc) { img.src = img.dataset.originalSrc; img.srcset = img.dataset.originalSrcset || ''; }
  });

  /* ---------- Sticky add to cart ---------- */
  function initStickyATC() {
    const bar = $('[data-sticky-atc]');
    if (!bar || !$('[data-main-buy-buttons]') || !once(bar, 'Sticky')) return;
    const footer = $('.footer');
    let footerVisible = false;
    // Show only once the main Add to Cart button has scrolled above the viewport.
    // The button is looked up on every check because variant changes and apps re-render the buy area.
    const update = () => {
      const area = $('[data-main-buy-buttons]');
      const btn = area && ($('.product-form__submit', area) || area);
      const pastButtons = !!btn && btn.getBoundingClientRect().bottom < 0;
      const show = pastButtons && !footerVisible;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    };
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { ticking = false; update(); });
    }, { passive: true });
    if (footer) new IntersectionObserver(([entry]) => { footerVisible = entry.isIntersecting; update(); }).observe(footer);
    update();
    $('[data-sticky-atc-btn]', bar).addEventListener('click', (e) => {
      const form = document.getElementById(e.currentTarget.dataset.form);
      if (form) {
        if (form.requestSubmit) form.requestSubmit(); else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    });
  }

  /* ---------- Pickup availability ---------- */
  async function loadPickup(el) {
    if (!el || !el.dataset.variantId) return;
    try {
      const res = await fetch(`${el.dataset.baseUrl || '/'}variants/${el.dataset.variantId}/?section_id=pickup-availability`);
      const doc = parseHTML(await res.text());
      const content = $('[data-pickup-content]', doc);
      el.innerHTML = content ? content.outerHTML : '';
    } catch (e) { el.innerHTML = ''; }
  }
  function initPickup(root = document) {
    $$('[data-pickup-availability]', root).forEach((el) => { if (once(el, 'Pickup')) loadPickup(el); });
  }

  /* ---------- Recommendations ---------- */
  function initRecommendations(root = document) {
    $$('[data-product-recommendations]', root).forEach(async (el) => {
      if (!once(el, 'Recs') || !el.dataset.url || el.children.length) return;
      try {
        const res = await fetch(el.dataset.url);
        const doc = parseHTML(await res.text());
        const fresh = $('[data-product-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) { el.innerHTML = fresh.innerHTML; initScope(el); }
      } catch (e) { /* ignore */ }
    });
    $$('[data-complementary]', root).forEach(async (el) => {
      if (!once(el, 'Complementary')) return;
      try {
        const res = await fetch(el.dataset.url);
        const doc = parseHTML(await res.text());
        const list = $('.complementary-list', doc);
        if (list) {
          el.innerHTML = `<p class="eyebrow">${el.dataset.heading || ''}</p>${list.outerHTML}`;
        }
      } catch (e) { /* ignore */ }
    });
  }

  /* ---------- Recently viewed ---------- */
  const RECENT_KEY = 'bare:recently-viewed';
  function trackRecentlyViewed() {
    const main = $('.section-main-product [data-product-handle]');
    if (!main) return;
    const handle = main.dataset.productHandle;
    const list = storage.get(RECENT_KEY, []).filter((h) => h !== handle);
    list.unshift(handle);
    storage.set(RECENT_KEY, list.slice(0, 16));
  }
  function initRecentlyViewed(root = document) {
    $$('[data-recently-viewed]', root).forEach(async (section) => {
      if (!once(section, 'Recent')) return;
      const limit = parseInt(section.dataset.limit, 10) || 4;
      const handles = storage.get(RECENT_KEY, []).filter((h) => h && h !== section.dataset.current).slice(0, limit);
      if (!handles.length) return;
      const list = $('[data-recently-viewed-list]', section);
      const results = await Promise.all(handles.map((h) => fetch(`${routes.root}products/${h}?view=card`).then((r) => (r.ok ? r.text() : '')).catch(() => '')));
      const html = results.filter((r) => r.includes('card')).join('');
      if (!html) return;
      list.innerHTML = html;
      section.hidden = false;
    });
  }

  /* ---------- Predictive search ---------- */
  function initPredictiveSearch() {
    const input = $('[data-predictive-search-input]');
    const results = $('[data-predictive-search-results]');
    if (!input || !results || !once(input, 'Predictive')) return;
    const drawer = input.closest('.search-drawer');
    let controller;
    const run = debounce(async () => {
      const q = input.value.trim();
      if (!q) { results.innerHTML = ''; if (drawer) drawer.classList.remove('has-results'); return; }
      if (controller) controller.abort();
      controller = new AbortController();
      try {
        const url = `${routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query,page,article&resources[limit]=8&resources[options][unavailable_products]=last&section_id=predictive-search`;
        const res = await fetch(url, { signal: controller.signal });
        const doc = parseHTML(await res.text());
        const response = $('[data-predictive-search-response]', doc);
        results.innerHTML = response ? response.outerHTML : `<p class="predictive__empty">${strings.noResults}</p>`;
        if (drawer) drawer.classList.add('has-results');
      } catch (e) { /* aborted */ }
    }, 250);
    input.addEventListener('input', run);
  }

  /* ---------- Facets (filtering & sorting) ---------- */
  function initFacets(root = document) {
    $$('[data-collection-section]', root).forEach((section) => {
      if (!once(section, 'Facets')) return;
      const sectionId = section.dataset.sectionId;
      let controller;

      const swap = (doc) => {
        ['[data-collection-results]', '[data-facets-active]', '[data-product-count]'].forEach((sel) => {
          const fresh = $(sel, doc);
          const current = $(sel, section);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        const freshForm = $('[data-facets-form-wrapper]', doc);
        const currentForm = $('[data-facets-form-wrapper]', section);
        if (freshForm && currentForm) {
          const openStates = $$('details', currentForm).map((d) => d.open);
          currentForm.innerHTML = freshForm.innerHTML;
          $$('details', currentForm).forEach((d, i) => { if (openStates[i] !== undefined) d.open = openStates[i]; });
        }
        const freshCount = $('.toolbar__count', doc);
        const filterBtn = $('.toolbar__filter', section);
        if (filterBtn) {
          const existing = $('.toolbar__count', filterBtn);
          if (existing) existing.remove();
          if (freshCount) filterBtn.appendChild(freshCount.cloneNode(true));
        }
        applyGridPreference(section);
        initLoadMore(section);
        revealIn(section);
      };

      const load = async (url) => {
        const results = $('[data-collection-results]', section);
        if (results) results.classList.add('is-loading');
        if (controller) controller.abort();
        controller = new AbortController();
        try {
          const fetchUrl = new URL(url, window.location.origin);
          fetchUrl.searchParams.set('section_id', sectionId);
          const res = await fetch(fetchUrl.toString(), { signal: controller.signal });
          swap(parseHTML(await res.text()));
          const pageUrl = new URL(url, window.location.origin);
          pageUrl.searchParams.delete('section_id');
          window.history.replaceState({}, '', pageUrl.toString());
        } catch (e) {
          if (e.name !== 'AbortError') window.location.href = url;
        } finally {
          if (results) results.classList.remove('is-loading');
        }
      };

      const fromForm = (form) => {
        const params = new URLSearchParams();
        new FormData(form).forEach((value, key) => { if (value !== '') params.append(key, value); });
        return `${window.location.pathname}?${params.toString()}`;
      };

      const onInput = debounce((form) => load(fromForm(form)), 450);
      section.addEventListener('change', (e) => {
        const form = e.target.closest('[data-facet-form]');
        if (form && e.target.matches('[data-facet-input]')) {
          if (e.target.type === 'number') onInput(form); else load(fromForm(form));
        }
      });
      section.addEventListener('input', (e) => {
        const form = e.target.closest('[data-facet-form]');
        if (form && e.target.type === 'number') onInput(form);
      });
      section.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-facet-form]');
        if (form) { e.preventDefault(); load(fromForm(form)); }
      });
      section.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (link) { e.preventDefault(); load(link.href); }
      });

      // Grid toggles
      section.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-grid-toggle]');
        if (!btn) return;
        storage.set('bare:grid', btn.dataset.gridToggle);
        applyGridPreference(section);
      });
      applyGridPreference(section);
      initLoadMore(section);
    });
  }

  function applyGridPreference(section) {
    const pref = storage.get('bare:grid', null);
    const grid = $('[data-product-grid]', section);
    if (!grid) return;
    grid.classList.remove('cols-2', 'cols-3', 'cols-4');
    $$('[data-grid-toggle]', section).forEach((b) => b.classList.remove('is-active'));
    if (pref) {
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      const applies = isMobile ? pref === '2' : pref !== '2';
      if (applies) {
        grid.classList.add(`cols-${pref}`);
        const btn = $(`[data-grid-toggle="${pref}"]`, section);
        if (btn) btn.classList.add('is-active');
      }
    }
  }

  /* ---------- Load more & infinite scroll ---------- */
  function initLoadMore(root = document) {
    $$('[data-load-more]', root).forEach((wrap) => {
      if (!once(wrap, 'LoadMore')) return;
      const link = $('[data-load-more-url]', wrap);
      if (!link) return;
      const sectionId = wrap.dataset.sectionId;
      let busy = false;
      const loadNext = async () => {
        if (busy) return;
        busy = true;
        link.classList.add('is-loading');
        try {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('section_id', sectionId);
          const res = await fetch(url.toString());
          const doc = parseHTML(await res.text());
          const grid = wrap.parentElement.querySelector('[data-product-grid]');
          const freshGrid = $('[data-product-grid]', doc);
          if (grid && freshGrid) {
            const items = Array.from(freshGrid.children);
            items.forEach((item) => grid.appendChild(item));
            revealIn(grid);
          }
          const freshWrap = $('[data-load-more]', doc);
          if (freshWrap) {
            wrap.replaceWith(freshWrap);
            initLoadMore(freshWrap.parentElement);
          } else {
            wrap.remove();
          }
        } catch (e) {
          window.location.href = link.href;
        }
      };
      link.addEventListener('click', (e) => { e.preventDefault(); loadNext(); });
      if (wrap.dataset.mode === 'infinite' && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { io.disconnect(); loadNext(); } }, { rootMargin: '400px' });
        io.observe(wrap);
      }
    });
  }

  /* ---------- Countdown ---------- */
  function initCountdowns(root = document) {
    $$('[data-countdown]', root).forEach((el) => {
      if (!once(el, 'Countdown')) return;
      const end = new Date(el.dataset.countdown).getTime();
      if (Number.isNaN(end)) return;
      const parts = {
        d: $('[data-countdown-days]', el), h: $('[data-countdown-hours]', el),
        m: $('[data-countdown-minutes]', el), s: $('[data-countdown-seconds]', el)
      };
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const diff = end - Date.now();
        if (diff <= 0) {
          clearInterval(timer);
          el.classList.add('is-expired');
          if (el.dataset.expiredText) el.insertAdjacentHTML('beforeend', `<p class="countdown__expired">${el.dataset.expiredText}</p>`);
          return;
        }
        parts.d.textContent = pad(Math.floor(diff / 86400000));
        parts.h.textContent = pad(Math.floor((diff / 3600000) % 24));
        parts.m.textContent = pad(Math.floor((diff / 60000) % 60));
        parts.s.textContent = pad(Math.floor((diff / 1000) % 60));
      };
      const timer = setInterval(tick, 1000);
      tick();
    });
  }

  /* ---------- Newsletter popup ---------- */
  // Never opens on load: waits for a time delay OR a scroll depth (whichever comes first),
  // and stays hidden for N days once closed or submitted.
  function initNewsletterPopup() {
    const popup = $('[data-newsletter-popup]');
    if (!popup || !once(popup, 'Popup')) return;
    if (window.Shopify && Shopify.designMode) return;
    const key = 'bare:popup-dismissed';
    const days = parseInt(popup.dataset.frequency, 10) || 7;
    const remember = (d = days) => storage.set(key, Date.now() + d * 864e5);
    const posted = /customer_posted=true/.test(window.location.search) && $('[data-popup-success]', popup);
    if (posted) { openOverlay(popup); remember(365); return; }
    const until = storage.get(key, 0);
    if (until && Date.now() < until) return;

    $$('[data-modal-close]', popup).forEach((btn) => btn.addEventListener('click', () => remember()));
    const form = $('form', popup);
    if (form) form.addEventListener('submit', () => remember());

    let shown = false;
    let timer;
    const delay = (parseInt(popup.dataset.delay, 10) || 25) * 1000;
    const scrollTarget = (parseInt(popup.dataset.scroll, 10) || 50) / 100;
    const show = () => {
      if (shown) return;
      shown = true;
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      if ($('[data-drawer].is-open, [data-modal].is-open')) return;
      openOverlay(popup);
    };
    const onScroll = debounce(() => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= scrollTarget) show();
    }, 100);
    timer = setTimeout(show, delay);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  document.addEventListener('click', async (e) => {
    const copy = e.target.closest('[data-copy]');
    if (copy) {
      try { await navigator.clipboard.writeText(copy.dataset.copy); toast(strings.copied); } catch (err) { /* ignore */ }
      return;
    }
    const share = e.target.closest('[data-share]');
    if (share) {
      const data = { title: share.dataset.shareTitle, url: share.dataset.shareUrl };
      if (navigator.share) { navigator.share(data).catch(() => {}); return; }
      try { await navigator.clipboard.writeText(data.url); toast(strings.copied); } catch (err) { /* ignore */ }
      return;
    }
    const play = e.target.closest('[data-video-play]');
    if (play) {
      const wrap = play.closest('[data-video]');
      const tpl = $('[data-video-template]', wrap);
      if (tpl) { play.replaceWith(tpl.content.cloneNode(true)); }
    }
  });

  /* ---------- Lookbook ---------- */
  function initLookbooks(root = document) {
    $$('[data-lookbook]', root).forEach((book) => {
      if (!once(book, 'Lookbook')) return;
      const spots = $$('[data-hotspot]', book);
      const select = (id) => {
        spots.forEach((s) => s.classList.toggle('is-active', s.dataset.hotspot === id));
        $$('[data-hotspot-target]', book).forEach((p) => p.classList.toggle('is-active', p.dataset.hotspotTarget === id));
      };
      spots.forEach((s) => {
        s.addEventListener('click', () => select(s.dataset.hotspot));
        s.addEventListener('mouseenter', () => select(s.dataset.hotspot));
      });
      if (spots[0]) select(spots[0].dataset.hotspot);
      book.selectHotspot = select;
    });
  }

  /* ---------- Reveal on scroll ---------- */
  let revealObserver;
  function revealIn(root = document) {
    if (!root || !document.body.classList.contains('animations-on')) return;
    const els = $$('.reveal:not(.is-revealed)', root);
    if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-revealed')); return; }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); revealObserver.unobserve(entry.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px' });
    }
    els.forEach((el) => {
      const siblings = el.parentElement ? Array.from(el.parentElement.children) : [];
      el.style.setProperty('--reveal-delay', `${Math.min(siblings.indexOf(el) % 6, 5) * 0.06}s`);
      revealObserver.observe(el);
    });
  }

  /* ---------- Video: hero + reels (play only when visible, respect data saver) ---------- */
  const prefersLessMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches || (navigator.connection && navigator.connection.saveData);
  let videoObserver;
  function initVideos(root = document) {
    const videos = $$('video[data-hero-video], video[data-reel-video]', root);
    if (!videos.length) return;
    if (prefersLessMotion()) {
      videos.forEach((v) => { v.removeAttribute('autoplay'); v.pause(); });
      return;
    }
    if (!('IntersectionObserver' in window)) return;
    if (!videoObserver) {
      videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          const slide = v.closest('[data-slide]');
          const inactiveSlide = slide && !slide.classList.contains('is-active');
          if (entry.isIntersecting && !inactiveSlide) {
            if (v.preload === 'none') v.preload = 'metadata';
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
      }, { threshold: 0.35 });
    }
    videos.forEach((v) => { if (once(v, 'Video')) videoObserver.observe(v); });
  }

  /* ---------- Checkout (Shopflo or Shopify) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-shopflo-checkout]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    if (typeof window.handleFloCheckoutBtn === 'function') {
      window.handleFloCheckoutBtn();
    } else {
      window.location.href = '/checkout';
    }
  });
  document.addEventListener('change', (e) => {
    const terms = e.target.closest('[data-cart-terms]');
    if (!terms) return;
    const form = terms.closest('form');
    $$('.checkout-btn', form || document).forEach((b) => { b.disabled = !terms.checked; });
  });

  /* ---------- Delivery estimate ---------- */
  function initDeliveryEstimates(root = document) {
    $$('[data-delivery-estimate]', root).forEach((el) => {
      if (!once(el, 'Delivery')) return;
      const out = $('[data-delivery-range]', el);
      const fmt = (d) => d.toLocaleDateString(document.documentElement.lang || 'en', { weekday: 'short', day: 'numeric', month: 'short' });
      const add = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d; };
      const min = parseInt(el.dataset.min, 10) || 4;
      const max = Math.max(min, parseInt(el.dataset.max, 10) || 7);
      if (out) out.textContent = `${fmt(add(min))} – ${fmt(add(max))}`;
    });
  }

  /* ---------- Back to top ---------- */
  function initBackToTop() {
    const btn = $('[data-back-to-top]');
    if (!btn || !once(btn, 'Top')) return;
    window.addEventListener('scroll', debounce(() => btn.classList.toggle('is-visible', window.scrollY > window.innerHeight * 1.5), 50), { passive: true });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  /* ---------- Init ---------- */
  function initScope(root = document) {
    initAnnouncement(root);
    initSlideshows(root);
    initSliders(root);
    initTabs(root);
    initVariantPickers(root);
    initGalleries(root);
    initPickup(root);
    initRecommendations(root);
    initRecentlyViewed(root);
    initFacets(root);
    initLoadMore(root);
    initCountdowns(root);
    initLookbooks(root);
    initDeliveryEstimates(root);
    initVideos(root);
    revealIn(root);
  }

  function init() {
    initHeader();
    initLightbox();
    initPredictiveSearch();
    initStickyATC();
    initNewsletterPopup();
    initBackToTop();
    trackRecentlyViewed();
    initScope(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  /* ---------- Theme editor ---------- */
  document.addEventListener('shopify:section:load', (e) => {
    initHeader();
    initStickyATC();
    initScope(e.target);
  });
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    const hero = block.closest('[data-slideshow]');
    if (hero && hero.goTo) hero.goTo($$('[data-slide]', hero).indexOf(block));
    const bar = block.closest('[data-announcement]');
    if (bar && bar.goTo) bar.goTo($$('[data-announcement-slide]', bar).indexOf(block));
    const book = block.closest('[data-lookbook]');
    if (book && book.selectHotspot && block.dataset.hotspot) book.selectHotspot(block.dataset.hotspot);
  });
  document.addEventListener('shopify:section:select', (e) => {
    const popup = $('[data-newsletter-popup]', e.target);
    if (popup) openOverlay(popup);
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const popup = $('[data-newsletter-popup]', e.target);
    if (popup) closeOverlay(popup);
  });

  window.BARE = { cart, formatMoney, openDrawer, toast };
})();
